import React, { useState } from 'react';

import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import DnsIcon from '@mui/icons-material/Dns';
import GroupsIcon from '@mui/icons-material/Groups';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export default function Support() {
  return (
    <div style={styles.wrapper}>
      <div style={styles.hero}>
        <h1 style={styles.title}>Support & FAQ</h1>
        <p style={styles.subtitle}>
          Implementing a Comprehensive Data Management Solution Doesn’t Have to
          Be Complicated
        </p>
      </div>

      <div style={styles.container}>
        <Category title="General" icon={<GroupsIcon />} data={generalFAQs} />
        <Category title="Support" icon={<SecurityIcon />} data={supportFAQs} />
        <Category
          title="Integration & Data Import/Export"
          icon={<StorageIcon />}
          data={integrationFAQs}
        />
        <Category title="IT" icon={<DnsIcon />} data={itFAQs} />
        <Category
          title="Prospects"
          icon={<SettingsIcon />}
          data={prospectFAQs}
        />
      </div>
    </div>
  );
}

/* ---------- CATEGORY ---------- */

function Category({ title, icon, data }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={styles.category}>
      <div style={styles.categoryHeader} onClick={() => setOpen(!open)}>
        <div style={styles.categoryLeft}>
          <span style={styles.icon}>{icon}</span>
          <h2 style={styles.categoryTitle}>{title}</h2>
        </div>
        <ExpandMoreIcon
          style={{
            transition: '0.3s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </div>

      {open && (
        <div style={styles.faqGrid}>
          {data.map((f, i) => (
            <div key={i} style={styles.card}>
              <h4 style={styles.question}>{f.q}</h4>
              <p style={styles.answer}>{f.a}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- DATA (UNCHANGED CONTENT) ---------- */

const generalFAQs = [
  {
    q: 'Who is PeakLabsPro?',
    a: 'PeakLabsPro provides high-end asset management software supporting companies and agencies, operating out of Cedar Rapids, Iowa.',
  },
  {
    q: 'What products does PeakLabsPro offer?',
    a: 'Any type of data management (shop system, property management) with customizable workflows and dashboards.',
  },
  {
    q: 'Who does PeakLabsPro serve?',
    a: 'Any sset-heavy organizations who wants to modernize from paper and pencil.',
  },
  {
    q: 'Is there a mobile app?',
    a: 'Yes, there is an iOS and Android mobile app.',
  },
  {
    q: 'How often are updates released?',
    a: 'Releases come regulaly monthly or as needed',
  },
  {
    q: 'What type of data should I track?',
    a: `Track anything that impacts cost, accountability, or efficiency. At minimum, monitor inflow (purchases, transfers), 
    outflow (usage, disposals), and accurate counts. Also consider maintenance history, lifecycle dates, warranties, inspections, 
    and approvals. If it affects money, risk, or operations; track it!`,
  },
];

const supportFAQs = [
  {
    q: 'How can I contact support?',
    a: 'Support is available 8am–6pm CST Monday–Friday. Phone: 319-540-9833 | Email: support@neehaw.com',
  },
  {
    q: 'What does implementation look like?',
    a: 'Discovery → Development → Go Live & Training → Sustainment.',
  },
  { q: 'Long-term contract required?', a: 'No. Annual subscription.' },
  {
    q: 'How do permissions work?',
    a: 'Custom security and permissions can be configured.',
  },
  {
    q: 'Do we keep our data if we leave?',
    a: 'Yes. Customers always have access and ownership of their data.',
  },
];

const integrationFAQs = [
  { q: 'Import fuel transactions?', a: 'Yes.' },
  { q: 'GPS mileage updates?', a: 'Yes.' },
  { q: 'Import financial data?', a: 'Yes.' },
  {
    q: 'Custom reports?',
    a: 'Yes, we have stocked reports plus any customer requested customizations',
  },
  {
    q: 'Barcode integration?',
    a: 'Yes. Our mobile apps provide ability to scan and enter data for barcodes and RFID chips',
  },
];

const itFAQs = [
  { q: 'SaaS access level?', a: 'Application-level only.' },
  { q: 'Concurrent users?', a: 'Based on licenses purchased.' },
  //   { q: 'MFA support?', a: 'Yes.' },
  {
    q: 'Storage limits or unlimited records?',
    a: 'There is no fixed storage limit, but it is monitored appropriately',
  },
];

const prospectFAQs = [
  { q: 'Web-based?', a: 'Yes. Any device with internet.' },
  { q: 'Custom permissions?', a: 'Yes.' },
  { q: 'API available?', a: 'Yes.' },
  { q: 'Time to go live?', a: 'Depends on scope.' },
  { q: 'Cost?', a: 'Contact us for a customized quote.' },
];

/* ---------- STYLES ---------- */

const styles = {
  wrapper: {
    background: 'linear-gradient(135deg,#0f172a,#1e293b)',
    minHeight: '100vh',
    color: 'white',
    fontFamily: 'system-ui, sans-serif',
  },

  hero: {
    textAlign: 'center',
    padding: '100px 20px 60px',
  },

  title: { fontSize: 44, marginBottom: 10 },

  subtitle: { opacity: 0.7, fontSize: 18 },

  container: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '0 20px 80px',
  },

  category: {
    marginBottom: 30,
    borderRadius: 16,
    background: 'rgba(255,255,255,0.05)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },

  categoryHeader: {
    padding: '22px 25px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },

  categoryLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },

  categoryTitle: { fontSize: 20 },

  icon: {
    fontSize: 18,
    opacity: 0.8,
  },

  faqGrid: {
    display: 'grid',
    gap: 18,
    padding: 25,
    gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
  },

  card: {
    background: 'rgba(255,255,255,0.04)',
    padding: 20,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.05)',
    transition: '0.2s',
  },

  question: {
    fontSize: 15,
    marginBottom: 8,
    fontWeight: 600,
  },

  answer: {
    fontSize: 14,
    opacity: 0.75,
    lineHeight: 1.6,
  },
};
